import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import ClaimDetailDrawer from '../components/drawers/ClaimDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { AISidebar, AIInsightCard } from '../components/ai';
import { Search, Filter, Download, Calendar } from 'lucide-react';
const Claims = () => {
  const navigate = useNavigate();
  const {
    claims
  } = useAdminData();
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Filters
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [partnerFilter, setPartnerFilter] = useState('Tutti i Partner');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Get unique partners for filter
  const uniquePartners = useMemo(() => {
    const partners = claims.map(c => c.partner);
    return ['Tutti i Partner', ...Array.from(new Set(partners))];
  }, [claims]);
  // Filter claims with useMemo
  const filteredClaims = useMemo(() => {
    return claims.filter(claim => {
      const matchesStatus = statusFilter === 'Tutti' || claim.status === statusFilter;
      const matchesPartner = partnerFilter === 'Tutti i Partner' || claim.partner === partnerFilter;
      const matchesSearch = claim.id.toLowerCase().includes(searchTerm.toLowerCase()) || claim.userId.toLowerCase().includes(searchTerm.toLowerCase());
      // Date filtering
      let matchesDateFrom = true;
      if (dateFrom) {
        const claimDate = new Date(claim.date.split(' ')[0]);
        const fromDate = new Date(dateFrom);
        matchesDateFrom = claimDate >= fromDate;
      }
      let matchesDateTo = true;
      if (dateTo) {
        const claimDate = new Date(claim.date.split(' ')[0]);
        const toDate = new Date(dateTo);
        matchesDateTo = claimDate <= toDate;
      }
      return matchesStatus && matchesPartner && matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [claims, statusFilter, partnerFilter, searchTerm, dateFrom, dateTo]);
  const handleRowClick = (claimId: string) => {
    setSelectedClaimId(claimId);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedClaimId(null), 300);
  };
  const handleExportCSV = () => {
    console.log('Esportazione CSV avviata');
  };
  const handlePartnerClick = (e: React.MouseEvent, partnerId: string) => {
    e.stopPropagation();
    navigate(`/partners/${partnerId}`);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Utilizzato':
        return 'bg-green-100 text-green-800';
      case 'Riscattato':
        return 'bg-blue-100 text-blue-800';
      case 'Annullato':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-1556">
      <div className="w-20 flex-shrink-0" data-id="element-1557">
        <Sidebar data-id="element-1558" />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1559">
        <Topbar data-id="element-1560" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-1561">
          <PageHeader title="Riscatti" subtitle="Storico dei premi riscattati (dati pseudonimi)." primaryAction={{
          label: 'Esporta CSV',
          onClick: handleExportCSV,
          icon: <Download className="h-4 w-4 mr-2" data-id="element-1563" />
        }} data-id="element-1562" />

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-1564">
            <div className="relative flex-1 min-w-[300px]" data-id="element-1565">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-1566" />
              <input type="text" placeholder="Cerca ID riscatto o ID utente..." className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1567" />
            </div>

            {/* Date From */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2 gap-2" data-id="element-1568">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-1569" />
              <span className="text-sm text-gray-600" data-id="element-1570">Da:</span>
              <input type="date" className="bg-transparent text-sm focus:outline-none" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-1571" />
            </div>

            {/* Date To */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2 gap-2" data-id="element-1572">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-1573" />
              <span className="text-sm text-gray-600" data-id="element-1574">A:</span>
              <input type="date" className="bg-transparent text-sm focus:outline-none" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-1575" />
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-1576">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-1577" />
              <select className="bg-transparent text-sm focus:outline-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-1578">
                <option value="Tutti" data-id="element-1579">Tutti gli stati</option>
                <option value="Riscattato" data-id="element-1580">Riscattato</option>
                <option value="Utilizzato" data-id="element-1581">Utilizzato</option>
                <option value="Annullato" data-id="element-1582">Annullato</option>
              </select>
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-1583">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-1584" />
              <select className="bg-transparent text-sm focus:outline-none" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} data-id="element-1585">
                {uniquePartners.map(partner => <option key={partner} value={partner} data-id="element-1586">
                    {partner}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-1587">
            <div className="overflow-x-auto" data-id="element-1588">
              <table className="w-full" data-id="element-1589">
                <thead data-id="element-1590">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1591">
                    <th className="px-6 py-3" data-id="element-1592">ID Riscatto</th>
                    <th className="px-6 py-3" data-id="element-1593">Data/Ora</th>
                    <th className="px-6 py-3 whitespace-nowrap" data-id="element-1594">Partner</th>
                    <th className="px-6 py-3" data-id="element-1595">Premio</th>
                    <th className="px-6 py-3" data-id="element-1596">Crediti</th>
                    <th className="px-6 py-3" data-id="element-1597">Utente</th>
                    <th className="px-6 py-3" data-id="element-1598">Stato</th>
                    <th className="px-6 py-3" data-id="element-1599">Metodo</th>
                    <th className="px-6 py-3 text-right" data-id="element-1600">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1601">
                  {filteredClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(claim.id)} data-id="element-1602">
                      <td className="px-6 py-4" data-id="element-1603">
                        <span className="font-mono text-sm font-medium text-gray-900" data-id="element-1604">
                          {claim.id}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1605">
                        {claim.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1606">
                        <button onClick={e => handlePartnerClick(e, claim.partnerId)} className="text-sm font-medium text-green-600 hover:text-green-700 underline" data-id="element-1607">
                          {claim.partner}
                        </button>
                      </td>
                      <td className="px-6 py-4" data-id="element-1608">
                        <div className="font-medium text-gray-900" data-id="element-1609">
                          {claim.reward}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-1610">
                        {claim.diamonds}
                      </td>
                      <td className="px-6 py-4" data-id="element-1611">
                        <span className="font-mono text-sm text-gray-600" data-id="element-1612">
                          {claim.userId}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-1613">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`} data-id="element-1614">
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1615">
                        {claim.method}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-1616">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRowClick(claim.id);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-1617">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {filteredClaims.length === 0 && <div className="text-center py-12 text-gray-500" data-id="element-1618">
              <p data-id="element-1619">Nessun riscatto trovato con i filtri selezionati.</p>
            </div>}
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar data-id="element-1620">
        <AIInsightCard title="AI: Lettura del periodo" insights={['I riscatti aumentano soprattutto su cosmetici solidi zero-waste (+25%).', 'Picco di riscatti nelle sedi Milano Centro e Roma Cola di Rienzo (ultimi 7 giorni).', 'Set Hennetica: calo conversione. Considera riduzione costo a 50 crediti o consulenza gratuita inclusa.', 'Workshop Cosmetici Fai-da-Te: alta domanda. Aumenta disponibilità del 30%.']} ctaLabel="Apri premi" onCtaClick={() => navigate('/rewards')} lastUpdated="Ultimi 7 giorni" data-id="element-1621" />
      </AISidebar>

      {/* Detail Drawer */}
      <ClaimDetailDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} claimId={selectedClaimId} data-id="element-1622" />
    </div>;
};
export default Claims;